import { ProviderVerificationDetails as AppAccountProviderVerificationDetails } from "@fabric-types/app-account";
import { CogaDocument } from "@obg/common.registration/registration/form/coga/coga-registration-form.model";
import { BettingLimitType, Consents } from "@obg/ngx.common";
import { DepositLimitPeriod, Gender, Legal, MarketingOffers, Person, Subscriptions } from "@obg/ngx.core";

import { AamsDocument } from "../../sdk/registration/aams/aams-registration.model";

export interface RegistrationState {
	isBusy: boolean;
	isSuccessful?: boolean;
	lastStatus?: RegistrationStatusResponse;
	token?: string;
	hashedEmail?: string;
	registrationDetails?: RegistrationPayload;
	emailVerificationToken?: string;
	initialStep?: number;
	flow?: RegistrationFormFlow;
}

export const enum RegistrationStateType {
	InProgress,
	Completed,
	Failed,
	AwaitingAccountVerification,
	BlockedByIovation,
	TokenNotFound = 404,
}

export interface FailedStep {
	stepName: string;
	statusCode: number;
	message: string;
}

export interface RegistrationStatusResponse {
	status: string;
	reasons: string[];
	credential: Credential;
}

export interface RegistrationResponse {
	hashedEmail?: string;
	token: string;
	credential?: Credential;
}

export interface RegistrationRequest {
	language: string;
	timeZone: string;
	currency: string;
	person: Person;
	legal: Legal;
	credential: Credential;
	subscriptions: Subscriptions;
	affiliateMetadata: AffiliateMetadata[];
	iovationBlackBox?: string;
	fraudToken?: string;
	address?: Address;
	depositLimit?: DepositLimit;
	consents?: Partial<Consents>;
	productMetadata?: ProductMetadata;
	gamingLimit?: GamingLimit;
	sessionLimit?: SessionLimit;
	balanceLimit?: BalanceLimit;
	complexDepositLimit?: MultiDepositLimit;
	betLimit?: PeriodLimit;
	wageringLimit?: PeriodLimit;
	document?: AamsDocument | CogaDocument | Document;
	providerVerificationDetails?: AppAccountProviderVerificationDetails;
}

export interface BrRegistrationRequest extends Omit<RegistrationRequest, "person"> {
	flow?: RegistrationFormFlow;
	person: BrPerson;
}

interface BrPerson extends Omit<Person, "personalCode"> {
	personalCode?: string;
}

export interface Address {
	street?: string;
	zipCode?: string;
	city?: string;
	country?: string;
	province?: string;
	fiscalRegionCode?: number;
	houseNumber?: string;
	state?: string;
	additive?: string;
	optionalAddressLine?: string;
	departmentOfResidence?: string;
	district?: string;
	department?: string;
	residenceMunicipality?: string;
}

export interface RegistrationDetails {
	detail: RegistrationRequest;
	lastStatus: RegistrationStatusResponse;
}

export interface RegistrationVerificationResponse {
	customerId: string;
}

export interface AffiliateMetadata {
	affiliate: string;
	bannerTag: string;
}

export interface ProductMetadata {
	registeredFromProduct: string;
}

export interface Credential extends UserIdentity {
	password: string;
}

export interface UserIdentity {
	username: string;
	email: string;
	password?: string;
}

export interface DepositLimit {
	amount: number;
	depositLimitPeriod: DepositLimitPeriod;
	dailyAmount?: string;
	weeklyAmount?: string;
	monthlyAmount?: string;
}

export interface MultiDepositLimit {
	monthlyAmount: number;
}

export interface BettingLimit {
	bettingLimit: number;
	bettingLimitType: BettingLimitType;
}

export interface GamingLimit {
	bettingLimit?: number;
	betSizeLimit?: number;
	wageringLimit?: number;
	bettingLimitType?: BettingLimitType;
}

export interface SessionLimit {
	sessionLimitPeriod: number;
	amount: string;
}

export interface BalanceLimit {
	amount: string;
}

export interface PeriodLimit {
	limitValue: number;
	period: number;
}

export interface RegistrationPayload {
	credential: Credential;
	person: PersonDetails;
	additionalDetails: AdditionalDetails;
	legal: Legal;
	depositLimit?: DepositLimit;
	marketingOffers: MarketingOffers;
	iovationBlackBox: string;
	fraudToken?: string;
	consents?: Partial<Consents>;
	gamingLimit?: GamingLimit;
	sessionLimit?: SessionLimit;
	balanceLimit?: BalanceLimit;
	complexDepositLimit?: MultiDepositLimit;
	betLimit?: PeriodLimit;
	wageringLimit?: PeriodLimit;
	nationalDocument?: NationalDocument;
	repet?: boolean;
	combinedConsentsLOTBA?: boolean;
	combinedConsentsIPLYC?: boolean;
	combinedConsentsLPCSE?: boolean;
	document?: AamsDocument | CogaDocument | Document;
	providerVerificationDetails?: AppAccountProviderVerificationDetails;
}

export interface BrRegistrationPayload {
	credential: Credential;
	person: BrPersonDetails;
	legal: Legal;
	depositLimit?: DepositLimit;
	marketingOffers: MarketingOffers;
	iovationBlackBox: string;
	fraudToken?: string;
	consents?: Partial<Consents>;
	complexDepositLimit?: MultiDepositLimit;
	betLimit?: PeriodLimit;
	wageringLimit?: PeriodLimit;
	nationalDocument?: NationalDocument;
	repet?: boolean;
}

export interface CPFRegistrationPayload {
	credential: Credential;
	person: Partial<PersonDetails>;
	legal: Legal;
	depositLimit?: DepositLimit;
	consents?: Partial<Consents>;
	marketingOffers: MarketingOffers;
	iovationBlackBox: string;
	fraudToken?: string;
	nationalDocument?: NationalDocument;
	additionalDetails?: AdditionalDetails;
	flow: RegistrationFormFlow;
}

export interface QuickRegistrationPayload {
	email: string;
	country: string;
	currency: string;
	password: string;
	legal: Legal;
	iovationBlackBox: string;
	fraudToken?: string;
}

export interface PersonDetails {
	firstName: string;
	lastName: string;
	email?: string;
	nativeFirstName?: string;
	nativeLastName?: string;
	dateOfBirth: string;
	birthCountry?: string;
	gender: Gender;
	personalCode?: number | string;
	personalCodeType?: string;
	isPoliticallyExposed?: boolean;
	secondFirstName?: string;
	secondSurname?: string;
	secondLastName?: string;
	nationality?: string;
	occupation?: string;
	taxCode?: string;
	maritalStatus?: string;
	dniNumber?: string;
	isNotPoliticallyExposed?: boolean;
	dossierId?: string;
	processId?: string;
	infix?: string;
	birthCity?: string;
	bsnNumber?: string;
	iban?: string;
}

export interface BrPersonDetails extends PersonDetails {
	firstName: string;
	lastName: string;
}

export interface AdditionalDetails extends Address {
	currency: string;
	phoneNumber: string;
	phoneExtension: string;
	nationalIdNumber?: string;
	additive?: string;
	department?: string;
	municipality?: string;
	district?: string;
}

export interface PhoneValidationSettings {
	placeholderKey: string;
	minLength: number;
	maxLength: number;
	helperKey?: string;
	pattern?: string;
}

export interface CountrySettings {
	countryCode: string;
	isNativePersonNameEnabled?: boolean;
	isCurrencyLocked?: boolean;
	addressSettings?: ProvinceSettings;
}

export interface ProvinceSettings {
	countryCode?: string;
	showProvinceBeforeCountry?: boolean;
	autoSelectProvince?: boolean;
	hasCityList?: boolean;
	showCityMismatchError?: boolean;
	cityHasProvinceCode?: boolean;
	fetchProvincesFromContent?: boolean;
	getStateShortFormFromGooglePlaces?: boolean;
}

export interface NationalDocument {
	nationalDocumentNumber: string;
	nationalDocumentType: string;
	DocumentType: string;
	DNINumber: string;
	ExpireDate: string;
	IssueDate: string;
	IssuePlace: string;
	DocumentNumber: string;
	nationalDocumentTypeId: number;
}

export type SignicatGender = "1" | "2";

export interface SignicatCustomerConsumer {
	bin: string;
	city: string;
	country: string;
	dateofbirth: string;
	email: string;
	gender: SignicatGender;
	houseno: string;
	initials: string;
	legallastname: string;
	legallastnameprefix: string;
	partnerlastname: string;
	partnerlastnameprefix: string;
	postalcode: string;
	preferredlastname: string;
	preferredlastnameprefix: string;
	street: string;
	telephone: string;
}

export interface SignicatCustomerBankId {
	deliveredserviceid: string;
}

export interface SignicatCustomerIdin {
	requestedserviceid: string;
}

export interface SignicatCustomer {
	consumer: SignicatCustomerConsumer;
	bankid: SignicatCustomerBankId;
	idin: SignicatCustomerIdin;
	birthdate: string;
	exp: string;
	family_name: string;
	gender: SignicatGender;
	given_name: string;
	isTest: boolean;
	iss: string;
	locale: string;
	middle_name: string;
	name: string;
	provider: string;
	sub: string;
	zoneinfo: string;
}

export enum RegistrationFormFlow {
	Simple = "Simple",
	Complete = "Complete",
}

export interface Document {
	dniNumber?: string;
	documentNumber?: string;
	documentType?: string;
	expireDate?: Date;
	issueDate?: Date;
	issuePlace?: string;
}

export interface RNSFeatureFlag {
	enabled?: boolean;
}

export interface ProviderVerificationDetails {
	providerName: "Techsson.Authentication.Oidc.Host";
	tokens: { action: "authentication"; value: string }[];
}

export const enum RegistrationCustomEvent {
	obgRegistrationSuccess = "obgRegistrationSuccess",
	obgRegistrationFail = "obgRegistrationFail",
}
